import { isActiveRule, type CollectionData, type ViewDef } from '@tomo/core';
import { Badge, Button, EmptyState, IconButton, Illustration, Tooltip, type IllustrationName } from '@tomo/ui';
import { Eye, Plus, RotateCcw, Upload } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type DragEvent } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useCollection, useProject, useReadOnly, useSession } from '../../data/hooks.tsx';
import { useHotkey } from '../../lib/hotkeys.ts';
import { pickFiles } from '../../lib/files.ts';
import { Icon } from '../../lib/icons.tsx';
import { dataTransferHasFiles, uploadAssets } from '../assets/upload.ts';
import { IconPicker } from '../shell/IconPicker.tsx';
import { useOpenRecord } from '../windows/hooks.ts';
import { BoardView } from './BoardView.tsx';
import { GalleryView, ListView } from './GalleryView.tsx';
import { TableView } from './TableView.tsx';
import { itemNoun, useViewRecords } from './view-utils.ts';
import { ViewToolbar } from './ViewToolbar.tsx';
import './collections.css';

/** Illustration for an empty collection: every collection is the same kind of thing now. */
const EMPTY_ART: IllustrationName = 'codex';

export function CollectionPage() {
  const { collectionId } = useParams();
  const collection = useCollection(collectionId);
  const session = useSession();
  const project = useProject();
  const navigate = useNavigate();
  const readOnly = useReadOnly();

  if (!collection) {
    return <EmptyState art={<Illustration name="search" />} title="Collection not found" description="It may have been deleted." actions={<Button onClick={() => navigate(`/p/${session.id}`)}>Back to overview</Button>} />;
  }
  if (collection.deletedAt) {
    return (
      <EmptyState
        art={<Illustration name="trash" />}
        title={`${collection.name} is in the trash`}
        description={readOnly ? 'An editor can restore it from the trash.' : 'Restore it to keep working with its records.'}
        actions={
          !readOnly && (
            <Button variant="primary" icon={<RotateCcw />} onClick={() => project.restoreCollection(collection.id)}>
              Restore
            </Button>
          )
        }
      />
    );
  }
  return <CollectionScreen key={collection.id} collection={collection} />;
}

function CollectionScreen({ collection }: { collection: CollectionData }) {
  const project = useProject();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [dropping, setDropping] = useState(false);
  const dragDepth = useRef(0);
  const readOnly = useReadOnly();

  const view: ViewDef = collection.views.find((v) => v.id === params.get('view')) ?? collection.views[0]!;
  const records = useViewRecords(collection, view, search);
  // Any collection takes a file drop: pictures become covers, everything else goes in its Files field.
  const canDrop = !readOnly;
  const filtered = search.trim() !== '' || view.filters.some(isActiveRule);

  const selectView = (id: string) => {
    const next = new URLSearchParams(params);
    next.set('view', id);
    setParams(next);
  };

  // Records open in windows, which step through this view with previous/next.
  const openRecord = useOpenRecord();
  const open = useCallback((id: string) => openRecord(id, { collectionId: collection.id, viewId: view.id }), [openRecord, collection.id, view.id]);

  const create = useCallback((values?: Record<string, unknown>) => project.createRecord(collection.id, { title: '', values }), [project, collection.id]);

  const upload = async (files?: File[]) => {
    const list = files ?? (await pickFiles({ multiple: true }));
    const ids = await uploadAssets(project, collection, list);
    if (ids.length === 1) open(ids[0]!);
  };

  const newRecord = () => {
    if (readOnly) return;
    const id = create();
    open(id);
  };

  useHotkey('c', newRecord);

  const onDragEnter = (e: DragEvent) => {
    if (!canDrop || !dataTransferHasFiles(e.dataTransfer)) return;
    dragDepth.current++;
    setDropping(true);
  };
  const onDragLeave = () => {
    if (!canDrop) return;
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (!dragDepth.current) setDropping(false);
  };
  const onDrop = (e: DragEvent) => {
    if (!canDrop || !dataTransferHasFiles(e.dataTransfer)) return;
    e.preventDefault();
    dragDepth.current = 0;
    setDropping(false);
    void upload(Array.from(e.dataTransfer.files));
  };

  const empty = filtered ? (
    <EmptyState
      size="sm"
      art={<Illustration name="search" size={120} />}
      title="No matches"
      description="Nothing in this view matches your search or filters."
      actions={
        <Button
          size="sm"
          onClick={() => {
            setSearch('');
            project.updateView(collection.id, view.id, { filters: [] });
          }}
        >
          Clear search &amp; filters
        </Button>
      }
    />
  ) : (
    <EmptyState
      art={<Illustration name={EMPTY_ART} />}
      title={`No ${collection.name.toLowerCase()} yet`}
      description={
        readOnly
          ? 'Nothing has been added here yet.'
          : collection.description || `Create the first ${itemNoun(collection)}, or drop files here to make one from each.`
      }
      actions={
        !readOnly && (
          <>
            <Button variant="primary" icon={<Plus />} onClick={newRecord}>
              New {itemNoun(collection)}
            </Button>
            <Button variant="ghost" icon={<Upload />} onClick={() => void upload()}>
              Upload files
            </Button>
          </>
        )
      }
    />
  );

  const props = { collection, view, records, onOpen: open, onCreate: create, empty };

  return (
    <div className="collection" onDragEnter={onDragEnter} onDragLeave={onDragLeave} onDragOver={(e) => canDrop && e.preventDefault()} onDrop={onDrop}>
      <div className="collection__main">
        <CollectionHeader collection={collection} onNew={newRecord} onUpload={() => void upload()} count={records.length} readOnly={readOnly} />
        <ViewToolbar collection={collection} view={view} onSelectView={selectView} search={search} onSearch={setSearch} visible={records} />
        <div className="collection__body" data-view={view.type}>
          {view.type === 'table' && <TableView {...props} />}
          {view.type === 'board' && <BoardView {...props} />}
          {view.type === 'gallery' && <GalleryView {...props} onUpload={canDrop ? () => void upload() : undefined} />}
          {view.type === 'list' && <ListView {...props} />}
        </div>
        {dropping && (
          <div className="dropzone">
            <div className="dropzone__inner">
              <Upload size={28} />
              <strong>Drop to add to {collection.name}</strong>
              <span>Files are hashed and stored on this device.</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Icon, title, description and one quiet action. The icon is the button that changes it. */
function CollectionHeader({
  collection,
  onNew,
  onUpload,
  count,
  readOnly,
}: {
  collection: CollectionData;
  onNew: () => void;
  onUpload: () => void;
  count: number;
  readOnly: boolean;
}) {
  const project = useProject();
  const [name, setName] = useState(collection.name);
  useEffect(() => setName(collection.name), [collection.name]);
  const noun = itemNoun(collection);
  return (
    <header className="collection__header">
      {readOnly ? (
        <span className="collection__icon" data-color={collection.color}>
          <Icon token={collection.icon} size={22} />
        </span>
      ) : (
        <IconPicker
          value={collection.icon}
          color={collection.color}
          onColorChange={(color) => project.updateCollection(collection.id, { color })}
          onChange={(icon) => icon && project.updateCollection(collection.id, { icon })}
          trigger={
            <button type="button" className="collection__icon" data-color={collection.color} aria-label="Change icon and color" title="Change icon and color">
              <Icon token={collection.icon} size={22} />
            </button>
          }
        />
      )}
      <div className="collection__titles">
        <input
          className="collection__name"
          readOnly={readOnly}
          value={name}
          aria-label="Collection name"
          onChange={(e) => setName(e.target.value)}
          onBlur={() => (name.trim() ? project.renameCollection(collection.id, name) : setName(collection.name))}
          onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
        />
        <input
          className="collection__desc"
          readOnly={readOnly}
          defaultValue={collection.description}
          key={collection.description}
          placeholder={readOnly ? '' : 'Add a description…'}
          aria-label="Collection description"
          onBlur={(e) => e.target.value !== collection.description && project.updateCollection(collection.id, { description: e.target.value })}
          onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
        />
      </div>
      <div className="collection__actions">
        <span className="collection__count tabular">{count === 1 ? `1 ${noun}` : `${count} ${collection.name.toLowerCase()}`}</span>
        {readOnly ? (
          <Badge variant="outline" icon={<Eye />}>
            View only
          </Badge>
        ) : (
          <>
            <Tooltip content={`New ${noun}`} shortcut="c">
              <Button variant="ghost" size="sm" icon={<Plus />} onClick={onNew}>
                New {noun}
              </Button>
            </Tooltip>
            <Tooltip content="Upload files — one record per file">
              <IconButton label="Upload files" size="sm" onClick={onUpload}>
                <Upload />
              </IconButton>
            </Tooltip>
          </>
        )}
      </div>
    </header>
  );
}
